import React, { useEffect, useMemo, useState } from 'react'
import { SearchMd, FilterLines, Stars01 } from '@untitled-ui/icons-react'
import {
  MyButton,
  MyDataTable,
  MyColumn,
  MyDoubleCard,
} from '@interstellar-component'
import { useWorkforce } from '../../../Context'
import RiskAssessmentTable from '../../EmployeeDetailsSheet/Tabs/ReportsContent/components/Sidebar/RiskAssessmentTable'
import { levelToBadge } from '../../EmployeeDetailsSheet/adapters/workforceDetailAdapter'

const PAGE_SIZE = 10

const TOOLTIPS = {
  total: 'Total credit-bureau enquiries on file for this individual.',
  overall: 'Aggregated public-profile risk based on the 1m / 3m / 6m / 12m enquiry burst pattern.',
}

function formatDate(value) {
  if (!value) return '—'
  const d = new Date(String(value).replace(/\//g, '-'))
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function FootprintsTab() {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const { workforceDetailRiskSignals } = useWorkforce()

  const footprintSection = workforceDetailRiskSignals?.footprint
  const counts = footprintSection?.counts ?? {}
  const records = footprintSection?.records ?? []
  const aiTakeaway = footprintSection?.overview?.key_takeaway || null
  const overallRisk = footprintSection?.overview?.overall_risk
  const overallRiskBadge = levelToBadge(overallRisk?.level)

  const allRows = useMemo(
    () =>
      records.map((r, i) => ({
        id: i + 1,
        name: r.institute || '—',
        subtext: '',
        purpose: r.purpose || '—',
        date: formatDate(r.date),
      })),
    [records]
  )

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase()
    if (!q) return allRows
    return allRows.filter((r) => (
      r.name.toLowerCase().includes(q)
      || r.subtext.toLowerCase().includes(q)
      || r.purpose.toLowerCase().includes(q)
    ))
  }, [allRows, searchTerm])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE))

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1)
  }, [currentPage, totalPages])

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredRows.slice(start, start + PAGE_SIZE)
  }, [filteredRows, currentPage])

  const totalEnquiries = records.length

  const indicators = [
    {
      label: 'Total enquiries',
      value: String(totalEnquiries),
      tooltip: TOOLTIPS.total,
    },
    {
      label: 'Overall risk',
      badge: overallRisk?.level ? overallRiskBadge.badge : undefined,
      badgeColor: overallRiskBadge.badgeColor,
      tooltip: overallRisk?.rationale || TOOLTIPS.overall,
    },
  ]

  const metrics = [
    { label: '1 month', value: String(counts.count_1_month ?? 0) },
    { label: '3 months', value: String(counts.count_3_months ?? 0) },
    { label: '6 months', value: String(counts.count_6_months ?? 0) },
    { label: '12 months', value: String(counts.count_12_months ?? 0) },
  ]

  return (
    <div className="grid lg:grid-cols-12 gap-8 pt-4">
      {/* Sidebar */}
      <div className="lg:col-span-4 flex flex-col gap-8 h-full lg:border-r lg:border-gray-200 lg:pr-8">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            Overview
            <Stars01 className="h-5 w-5 text-brand/900" />
          </h3>
          <p className="text-sm-regular text-gray-600">
            Key insights from credit and background data.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <MyDoubleCard heading="Key takeaway">
            <p className="text-sm-regular text-gray-600 leading-relaxed">
              {aiTakeaway || 'No credit enquiry insights available yet.'}
            </p>
          </MyDoubleCard>

          <RiskAssessmentTable data={indicators} />
        </div>
      </div>

      {/* Main Table Content */}
      <div className="lg:col-span-8 flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-900">Credit enquiry activity</h3>
          <p className="text-sm-regular text-gray-600">
            Breakdown of active and historical credit facilities associated with this individual.
          </p>
        </div>

        {/* Metrics Group */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="flex flex-col gap-1 rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <span className="text-xs-semibold text-gray-500 uppercase tracking-wider">
                {metric.label}
              </span>
              <span className="text-2xl font-semibold text-gray-900">{metric.value}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-0 border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
          {/* Table Controls */}
          <div className="flex p-4 items-center justify-between border-b border-gray-200 gap-4">
            <div className="relative flex-1 max-w-sm">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchMd className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full rounded-lg border border-gray-300 bg-white p-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-brand/500 focus:outline-none focus:ring-1 focus:ring-brand/500"
                placeholder="Search for footprints"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <MyButton color="secondary" variant="outlined" size="md">
                <FilterLines className="h-4 w-4" />
                Filters
              </MyButton>
            </div>
          </div>

          {/* Table */}
          {pagedRows.length > 0 ? (
            <MyDataTable values={{ data: pagedRows }} className="border-none shadow-none">
              <MyColumn
                header="Institution"
                field="name"
                body={(row) => (
                  <div className="flex flex-col gap-0.5 py-1">
                    <span className="text-sm-semibold text-gray-900">{row.name}</span>
                    {row.subtext && (
                      <span className="text-xs text-gray-500 font-normal">{row.subtext}</span>
                    )}
                  </div>
                )}
              />
              <MyColumn
                header="Purpose"
                field="purpose"
                body={(row) => (
                  <span className="text-sm-regular text-gray-600 block py-1">{row.purpose}</span>
                )}
              />
              <MyColumn
                header="Enquiry Date"
                field="date"
                body={(row) => (
                  <span className="text-sm-regular text-gray-600 block py-1">{row.date}</span>
                )}
              />
            </MyDataTable>
          ) : (
            <div className="px-6 py-10 text-center text-sm-regular text-gray-500">
              No credit enquiry records found for this individual.
            </div>
          )}

          {/* Footer / Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200">
            <span className="text-sm-regular text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <MyButton
                color="secondary"
                variant="outlined"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </MyButton>
              <MyButton
                color="secondary"
                variant="outlined"
                size="sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </MyButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
